import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useReduxAuth';
import { hasuraClient } from '../utils/graphqlClient';
import {
  GET_FIRM_IDS_BY_USER_QUERY,
  GET_FIRM_IDS_BY_TEAM_QUERY,
} from '../graphql/queries/userFirms';

// Roles that see all company firms with no per-firm restriction
const UNRESTRICTED_ROLES = ['ROOT', 'DIRECTOR', 'GROUP_MANAGER'];

/**
 * Returns the firm IDs accessible to the current user.
 * - firmIds: null  → user is unrestricted (sees all company firms)
 * - firmIds: []    → restricted, no firms assigned
 * - firmIds: [..] → restricted to those firm IDs
 * - loading: true  → firm IDs not yet determined
 */
export const useAccessibleFirmIds = () => {
  const { user } = useAuth();
  const [firmIds, setFirmIds] = useState(null);
  const [loading, setLoading] = useState(true);

  const role = user?.role;
  const userId = user?.id;
  const teamId = user?.team_id;
  const isRestricted = !!role && !UNRESTRICTED_ROLES.includes(role);

  const fetchFirmIds = useCallback(async () => {
    if (!userId) return;

    if (!isRestricted) {
      setFirmIds(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      if (role === 'TEAM_MANAGER' && teamId) {
        const data = await hasuraClient.query(GET_FIRM_IDS_BY_TEAM_QUERY, {
          teamId: parseInt(teamId),
        });
        setFirmIds((data.user_firms || []).map(uf => uf.firm_id));
      } else {
        const data = await hasuraClient.query(GET_FIRM_IDS_BY_USER_QUERY, {
          userId: parseInt(userId),
        });
        setFirmIds((data.user_firms || []).map(uf => uf.firm_id));
      }
    } catch {
      setFirmIds([]);
    } finally {
      setLoading(false);
    }
  }, [isRestricted, role, userId, teamId]);

  useEffect(() => {
    fetchFirmIds();
  }, [fetchFirmIds]);

  return { firmIds, loading, isRestricted };
};
